import React, { useState, useRef, useMemo, memo, useCallback, useEffect } from 'react';
import CollapsibleTileComponent from '../collapsible-tile/collapsible-tile.component';
import { BusinessVertical, CollapsibleTile } from '@box-types';
import { DiscoverTilesGroup, DiscoverTilesOptions, getCollectionDiscoverCuisinesImpressionGAConfig } from '@box/utils';
import { useInjector } from '@box-react/hooks/use-injector';
import { AnalyticsService } from '@box-core/services';
import { LocationsService } from '@box-delivery/services';
import { Mousewheel, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { AngularWrapper } from '../angular-wrapper/angular-wrapper';
import { MatIcon } from '@angular/material/icon';
import { translate as t } from '@box-core/services/language.service';
import isEqual from 'lodash-es/isEqual';
import { Swiper as SwiperType } from 'swiper/types';

type DiscoverTilesProps<T> = {
  tiles: CollapsibleTile<T>[];
  options: DiscoverTilesOptions;
  businessVertical: BusinessVertical;
  onTileToggle?: (tile: CollapsibleTile<T>) => void;
};

function generateGroupKey<T>(tiles: CollapsibleTile<T>[], index: number, separator = '-'): string {
  if (!tiles?.length) return `empty-group-${index}`;
  const keys = tiles.map((tile) => tile?.key ?? '').join(separator);
  return `group-${index}-${keys}`;
}

function DiscoverTilesComponent<T>({ tiles, options, businessVertical, onTileToggle }: DiscoverTilesProps<T>) {
  const analyticsService = useInjector().get(AnalyticsService);
  const locationsService = useInjector().get(LocationsService);

  const prevEl = useRef<HTMLButtonElement>(null);
  const nextEl = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentTiles = useMemo(() => tiles ?? [], [tiles]);
  const title = useMemo(() => options?.title ?? '', [options?.title]);
  const size = useMemo(() => options?.size ?? 'large', [options?.size]);
  const groupSize = useMemo(() => options?.groupRows ?? 2, [options?.groupRows]);

  const [isNavigationHidden, setNavigationHidden] = useState(false);
  const [activeTile, setActiveTile] = useState(null);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);

  const groups = useMemo(() => {
    if (!currentTiles?.length) return [];
    if (!options?.groupRows) return currentTiles.map((tile) => ({ tiles: [tile] }));

    const groups: DiscoverTilesGroup<T>[] = [];
    for (let i = 0; i < currentTiles.length; i += groupSize) {
      groups.push({ tiles: currentTiles.slice(i, i + groupSize) });
    }
    return groups;
  }, [currentTiles, groupSize]);

  useEffect(() => {
    if (!currentTiles?.length) return;
    if (!options?.activeTileKey) return;
    const initialTile = currentTiles.find((tile: CollapsibleTile<T>) => tile.key === options.activeTileKey);
    handleTileClick(initialTile);
  }, []);

  useEffect(() => {
    if (swiperInstance?.navigation && prevEl?.current && nextEl?.current) {
      swiperInstance.navigation.init();
      swiperInstance.navigation.update();
    }
  }, [swiperInstance?.navigation, prevEl?.current, nextEl?.current]);

  const handleTileClick = useCallback(
    (selectedTile: CollapsibleTile<T>) => {
      setActiveTile((currentActiveTile) => {
        if (!selectedTile || selectedTile.disabled) {
          currentActiveTile !== null && onTileToggle?.(null);
          setNavigationHidden(false);
          return null;
        }

        if (currentActiveTile === selectedTile) {
          onTileToggle?.(null);
          setNavigationHidden(false);
          return null;
        } else {
          onTileToggle?.(selectedTile);
          setNavigationHidden(true);
          triggerAnalyticsEvent('select_promotion', selectedTile);
          return selectedTile;
        }
      });
    },
    [onTileToggle]
  );

  const setNavButtonContainerVisibility = useCallback(() => {
    if (!prevEl.current || !nextEl.current || !containerRef.current) return;

    const nextButtonIsDisabled = nextEl.current.classList.contains('swiper-button-disabled');
    const prevButtonIsDisabled = prevEl.current.classList.contains('swiper-button-disabled');

    if (nextButtonIsDisabled && prevButtonIsDisabled) {
      containerRef.current.classList.add('hidden');
    } else {
      containerRef.current.classList.remove('hidden');
    }
  }, [prevEl, nextEl, containerRef]);

  const triggerAnalyticsEvent = (eventName: string, tile: CollapsibleTile<T>) => {
    const promotionName = title;
    const isLandingPage = locationsService.isSeoLocationPage();

    const gaConfig = getCollectionDiscoverCuisinesImpressionGAConfig(
      businessVertical,
      promotionName,
      currentTiles,
      tile,
      isLandingPage
    );
    analyticsService.addGAEcommerceEvent(eventName, gaConfig);
  };

  return (
    <div className="box-swiper">
      <div className="swiper-section-header">
        <div className="swiper-section-header-content">
          <h3 className="swiper-section-title">{t(title)}</h3>
        </div>
        <div ref={containerRef} className="swiper-section-navigation" hidden={isNavigationHidden}>
          <button
            ref={prevEl}
            className="box-swiper-button-prev mdc-icon-button mat-mdc-icon-button mat-unthemed mat-mdc-button-base"
          >
            <AngularWrapper
              inputs={{ fontSet: 'material-icons-round' }}
              className="material-icons-round"
              component={MatIcon}
            >
              chevron_left
            </AngularWrapper>
          </button>

          <button
            ref={nextEl}
            className="box-swiper-button-next  mdc-icon-button mat-mdc-icon-button mat-unthemed mat-mdc-button-base"
          >
            <AngularWrapper
              inputs={{ fontSet: 'material-icons-round' }}
              className="material-icons-round"
              component={MatIcon}
            >
              chevron_right
            </AngularWrapper>
          </button>
        </div>
      </div>
      {activeTile ? (
        <div onClick={() => handleTileClick(activeTile)}>
          <CollapsibleTileComponent
            className="discover-tiles-collapsed-tile"
            tile={activeTile}
            collapsed={true}
            size={size}
          />
        </div>
      ) : (
        <>
          <Swiper
            onSwiper={setSwiperInstance}
            onUpdate={setNavButtonContainerVisibility}
            modules={[Navigation, Mousewheel]}
            navigation={{
              prevEl: prevEl?.current,
              nextEl: nextEl?.current
            }}
            mousewheel={{ forceToAxis: true, releaseOnEdges: false }}
            slidesPerView={'auto'}
            grabCursor={true}
            spaceBetween={16}
            className="discover-tiles-component-swiper"
          >
            <div className="swiper-wrapper discover-tiles-component-wrapper">
              {groups.map((group, index) => (
                <SwiperSlide
                  key={generateGroupKey(group.tiles, index)}
                  className={`swiper-slide discover-tiles-component-group-slide discover-tiles-component-group-slide-${
                    size ?? 'large'
                  }`}
                >
                  {group.tiles.map((tile: CollapsibleTile<T>) => (
                    <div
                      key={tile.key}
                      className={`collapsible-tile-size--${size ?? 'large'}`}
                      onClick={() => handleTileClick(tile)}
                    >
                      <CollapsibleTileComponent
                        className={`collapsible-tile-size--${size ?? 'large'}`}
                        tile={tile}
                        size={size}
                        collapsed={false}
                      />
                    </div>
                  ))}
                </SwiperSlide>
              ))}
            </div>
          </Swiper>
        </>
      )}
    </div>
  );
}

// secures from unecessary rerenders due to bad state management from the parent
const areEqual = (prevProps, nextProps) => {
  return isEqual(prevProps, nextProps);
};
export default memo(DiscoverTilesComponent, areEqual);
